import React from "react";
import clsx from "clsx";
import moment from "moment";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useHistory, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Snackbar,
  SvgIcon,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  MenuBook as MenuBookIcon,
  PlayArrow as PlayArrowIcon,
  Share as ShareIcon,
  Subject as SubjectIcon,
  YouTube as YouTubeIcon,
} from "@material-ui/icons";
import SongMenuButton from "components/SongMenuButton";
import NoLyricsWarning from "components/NoLyricsWarning";
import TemplateSelect from "routes/EventDetail/TemplateSelect";
import AddSongControl from "routes/EventDetail/AddSongControl";
import AddNoteControl from "routes/EventDetail/AddNoteControl";
import NoteRow from "routes/EventDetail/NoteRow";
import NotificationPermissionPrompt from "routes/EventDetail/NotificationPermissionPrompt";
import { UserContext } from "contexts/UserContext";
import { useSongThumbnailUrl } from "hooks/useSongThumbnailUrl";
import { EVENT_VIEW_NOTIFICATION_PROMPT_SHOWN_KEY } from "CONSTANTS";
import { endForNewStart } from "utils/eventTimes";
import { hasLyrics } from "utils/lyrics";
import { teamLabel } from "utils/team";

// Static per session (a browser either has the Web Share API or it
// doesn't) - checked once at module scope rather than on every render.
// Chrome/Safari on mobile support it; most desktop browsers don't, so
// this ends up being a reasonable mobile/desktop split in practice
// without hardcoding a screen-size check for what's really a capability
// check.
const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

// Helper text for each possible Event.status value, shown alongside
// statusEditor's select - statusChips covers read-only status display.
const statusHelperText = {
  draft: "Only visible to leaders/admins until published",
  published: "Visible to the team",
  cancelled: "Stays visible to the team, marked as cancelled, instead of disappearing",
};

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.background.paper,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
  },
  title: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  content: {
    paddingTop: theme.spacing(3),
    // Extra clearance so the last setlist row's menu isn't hidden behind
    // the fixed Edit FAB (same reasoning as SongProposalsContainer's pb={8}).
    paddingBottom: theme.spacing(9),
  },
  team: {
    display: "inline-block",
    border: "1px solid",
    padding: "3px 10px",
    borderRadius: "4px",
    marginBottom: theme.spacing(1),
  },
  info: {
    margin: theme.spacing(0.5, 0),
  },
  actionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  setlistHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  setlistCount: {
    color: theme.palette.text.secondary,
  },
  songList: {
    marginTop: theme.spacing(1),
  },
  thumbWrap: {
    position: "relative",
    width: 56,
    height: 32,
    borderRadius: 4,
    overflow: "hidden",
    flexShrink: 0,
    // Set explicitly rather than relying on ListItemAvatar's own
    // minWidth (56px, sized for a 40px circular Avatar) to produce this
    // gap as a side effect: that only holds at exactly 56px and does
    // nothing at other thumbnail sizes (e.g. 84px, wider than the
    // minWidth).
    marginRight: theme.spacing(1.5),
  },
  thumbLarge: {
    width: 84,
    height: 48,
    borderRadius: 6,
  },
  thumbImg: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  playOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.25)",
  },
  playIcon: {
    color: "#fff",
    fontSize: 16,
  },
  songBadge: {
    fontSize: 14,
    marginLeft: theme.spacing(0.75),
    verticalAlign: "text-bottom",
    color: theme.palette.text.secondary,
  },
  dragHandle: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.secondary,
    cursor: "grab",
    marginRight: theme.spacing(0.5),
    // Without this, a touch-drag on mobile fights the browser's own
    // scroll gesture on this element instead of triggering the drag.
    touchAction: "none",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(0.5),
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
  },
  unsortedLabel: {
    fontSize: 13,
    fontStyle: "italic",
    color: theme.palette.text.secondary,
  },
  emptyDropzone: {
    border: `1.5px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: "center",
    marginBottom: theme.spacing(1),
  },
  addChooserRow: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 2),
  },
  addChooserButton: {
    flex: 1,
  },
  // Mobile only (see isDesktop below) - the desktop layout has no
  // floating button at all, so there's no viewport-width edge case to
  // account for here anymore.
  fab: {
    position: "fixed",
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: 2,
  },
  // ---- Desktop layout ----
  // Same sticky app bar as mobile (see AppBar in the render below) -
  // only the content below it splits into three columns: sidebar,
  // setlist (the widest, kept in the middle since it's the main event),
  // and a status column for Details/checkboxes/Delete.
  desktopGrid: {
    display: "grid",
    gridTemplateColumns: "300px 2fr 350px",
    gap: theme.spacing(4),
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(5),
  },
  sidebar: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  sidebarActions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2.5),
  },
  sidebarButton: {
    justifyContent: "flex-start",
  },
  main: {
    minWidth: 0,
  },
  templateControl: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  sidebarField: {
    marginBottom: theme.spacing(2),
  },
  // Read-only display for the name/details/template/status fields
  // (plain viewer, or showEditControls false) - the actual editable
  // fields (nameEditor etc., below) don't use this at all.
  nameDisplay: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  nameText: {
    flex: 1,
    minWidth: 0,
    wordBreak: "break-word",
  },
  statusColumn: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  editToggleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  deleteButton: {
    color: "#f44336",
    borderColor: "#f44336",
    marginTop: theme.spacing(2),
    alignSelf: "flex-start",
  },
  notifyCheckbox: {
    marginLeft: 0,
  },
}));

// A real component (not a plain render-prop function like
// renderSongRow, below, which is called once per item inside the same
// parent render) - useSongThumbnailUrl needs its own component
// instance per song to call safely.
const SongThumbnail = ({ song, thumbLarge, classes }) => {
  const thumbnailUrl = useSongThumbnailUrl(song);
  return (
    <div className={clsx(classes.thumbWrap, thumbLarge && classes.thumbLarge)}>
      <img className={classes.thumbImg} src={thumbnailUrl} alt="" />
      {/* Only a real hint of actual video, not shown over a Spotify-only
          song's plain cover image (see useSongThumbnailUrl). */}
      {song.youtube_id && (
        <div className={classes.playOverlay}>
          <PlayArrowIcon className={classes.playIcon} />
        </div>
      )}
    </div>
  );
};

const durationString = (timeStart, timeEnd) => {
  const timeDuration = moment.duration(moment(timeEnd).diff(timeStart));
  let duration = [];
  if (timeDuration.days()) {
    duration.push(
      timeDuration.days() + (timeDuration.days() === 1 ? " day" : " days")
    );
  }
  if (timeDuration.hours()) {
    duration.push(
      timeDuration.hours() + (timeDuration.hours() === 1 ? " hour" : " hours")
    );
  }
  if (timeDuration.minutes()) {
    duration.push(
      timeDuration.minutes() +
        (timeDuration.minutes() === 1 ? " minute" : " minutes")
    );
  }
  duration = duration.join(", ");
  const lastComma = duration.lastIndexOf(",");
  if (lastComma > 0) {
    duration = duration.slice(0, lastComma) + " and" + duration.slice(lastComma + 1);
  }
  return duration;
};

// Combines songs and notes (each already carrying its own `section` id
// from the API) into one list, tagged with itemType and sorted by their
// shared `order`, then buckets that combined list by the applied
// template's own sections, in that template's order, plus a trailing
// "Unsorted" bucket for anything not in one of those sections (no
// section yet, or a stale/removed one). Songs and notes share one order
// space per event - see EventViewSet.reorder_items - so sorting the
// merged list by `order` is enough to interleave them correctly, no
// separate merge logic needed. `setlistTemplate` may be null/undefined
// (no template applied) - that's just the zero-sections case, so
// everything lands in `unsorted`, same as a stale/removed section id
// would with a template applied.
const groupItemsByTemplate = (songs, notes, setlistTemplate) => {
  const items = [
    ...songs.map((song) => ({ ...song, itemType: "song" })),
    ...notes.map((note) => ({ ...note, itemType: "note" })),
  ].sort((a, b) => a.order - b.order);
  const templateSections = setlistTemplate ? setlistTemplate.sections : [];
  const sectionIds = templateSections.map((section) => section.id);
  const sections = templateSections.map((section) => ({
    ...section,
    items: items.filter((item) => item.section === section.id),
  }));
  const unsorted = items.filter((item) => !sectionIds.includes(item.section));
  return { sections, unsorted };
};

export default ({
  eventModal,
  closeEventModal,
  menuActions,
  onReorderItems,
  onChangeTemplate,
  onAddSong,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  onUpdateEvent,
  onDeleteEvent,
  onNotifyTeam,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // Same breakpoint Navigationbar.jsx already switches its own layout
  // on. Desktop and mobile render genuinely different JSX below, not
  // one tree reflowing via CSS - the two layouts differ structurally
  // (sidebar + grid vs. a single stacked column with a floating button).
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const userContext = React.useContext(UserContext);
  const user = userContext.user;
  const history = useHistory();
  const location = useLocation();
  // Generalized beyond just "Copied to clipboard!" - desktop's inline
  // fields reuse the same Snackbar to confirm a save (see showSnackbar
  // below), since there's no explicit Save button to do that instead.
  // severity drives the Alert's color: green ("success", the default) or
  // red ("error") - every save on this page reports one or the other
  // instead of assuming success and going silent on failure.
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });
  // Opt-in, not the default, even for someone who *can* edit - a normal
  // viewer (canEdit or not) always lands on the plain read view; editing
  // is a deliberate switch, not what "having permission" already implies.
  // A not-yet-created event (no id yet) has nothing to view, so it starts
  // in edit mode instead of showing an empty read view first.
  const [isEditing, setIsEditing] = React.useState(!eventModal.id);
  // Local drafts for the inline-editable name/time/details fields, so
  // typing feels immediate instead of round-tripping through the
  // container on every keystroke. Committed onBlur (see commitField)
  // and re-synced from eventModal whenever it changes out from under
  // them (initial load, or this event's own save response) - the same
  // "server value can win" idea NoteRow already uses for its own edit
  // state. All of these, plus the template picker and status select,
  // are directly editable the whole time showEditControls is true - no
  // separate per-field click-to-activate step. That idiom existed to
  // guard against an accidental change from a stray click, but entering
  // edit mode is already the deliberate action guarding against that;
  // a second layer of "click to activate" per field on top of it was
  // just redundant friction.
  const [nameDraft, setNameDraft] = React.useState(eventModal.title || "");
  const [detailsDraft, setDetailsDraft] = React.useState(eventModal.details || "");
  const toDatetimeLocal = (date) => (date ? date.toISOString().slice(0, -5) : "");
  const [timeStartDraft, setTimeStartDraft] = React.useState(toDatetimeLocal(eventModal.timeStart));
  const [timeEndDraft, setTimeEndDraft] = React.useState(toDatetimeLocal(eventModal.timeEnd));
  React.useEffect(() => {
    setNameDraft(eventModal.title || "");
    setDetailsDraft(eventModal.details || "");
    setTimeStartDraft(toDatetimeLocal(eventModal.timeStart));
    setTimeEndDraft(toDatetimeLocal(eventModal.timeEnd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventModal.title, eventModal.details, eventModal.timeStart, eventModal.timeEnd]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  // Drafts are blocked at the API for anyone but a leader/admin (see
  // EventViewSet.retrieve) - sharing one's link means handing someone
  // a link that, for most people it reaches, just won't open. Worth a
  // pause before that happens, not just a toast after.
  const [confirmShareDraftOpen, setConfirmShareDraftOpen] = React.useState(false);

  // Draft -> published is the one moment NotificationPermissionPrompt
  // shows itself (if it's even still eligible to - see its own
  // docstring) - covers both publishing an existing draft and picking
  // "Published" as the very first edit on a brand-new event (either way,
  // eventModal.status transitions here, this effect doesn't care which
  // path got it there).
  const previousStatusRef = React.useRef(eventModal.status);
  const [justPublished, setJustPublished] = React.useState(false);
  React.useEffect(() => {
    if (previousStatusRef.current !== "published" && eventModal.status === "published") {
      setJustPublished(true);
    }
    previousStatusRef.current = eventModal.status;
  }, [eventModal.status]);

  // A regular (non-leader) member never triggers the draft->published
  // transition above - only a leader/admin publishes - so without this,
  // they'd only ever find the notifications toggle by digging into
  // Account Settings themselves. Fires once per device, the first time
  // a non-leader opens any published event - not every view, a member
  // opens events far more often than a leader publishes one, and
  // re-asking on every single click would just be annoying. Tracked
  // with its own localStorage flag, independent of the prompt's own
  // "Not now" vs "Don't ask again" semantics (see
  // NotificationPermissionPrompt's own docstring) - this is about
  // whether to trigger it at all, not what happens once it's shown.
  const [viewerPromptEligible, setViewerPromptEligible] = React.useState(false);
  React.useEffect(() => {
    if (user.is_staff || user.is_team_leader) return;
    if (eventModal.status !== "published") return;
    if (localStorage.getItem(EVENT_VIEW_NOTIFICATION_PROMPT_SHOWN_KEY)) return;
    localStorage.setItem(EVENT_VIEW_NOTIFICATION_PROMPT_SHOWN_KEY, "1");
    setViewerPromptEligible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventModal.status]);
  const closeNotificationPrompt = () => {
    setJustPublished(false);
    setViewerPromptEligible(false);
  };

  // Manual override for the automatic (cooldown-gated) push a save
  // already triggers - see maybe_notify_event's docstring
  // (song_proposals/push.py) for why this exists: several small edits
  // that individually wouldn't re-notify, or just a reminder nudge with
  // nothing new to say. Off by default, every time - checking it
  // doesn't notify by itself, it just flags the *next* save (whichever
  // field that ends up being) to also notify, then resets back to
  // unchecked, so it never lingers into a later, unrelated edit.
  const [notifyOnSave, setNotifyOnSave] = React.useState(false);

  // Wraps *any* save-like action below (onUpdateEvent, but also
  // reordering/adding a song or note, changing the template, editing or
  // deleting a note - every one of them is a way this event visibly
  // changes) so the checkbox means what it says regardless of which
  // field the very next save turns out to touch. Each call site already
  // wraps its own .then(showSnackbar(...))/.catch(...) around whichever
  // of these it calls, so this only adds the notify-on-save behavior
  // without touching any of that. Silent on success (stacking a second
  // snackbar message right after the caller's own would just clobber
  // it) - only speaks up if the notify itself failed, since that'd
  // otherwise be invisible.
  const withMaybeNotify = (action) => (...args) => {
    const shouldNotify = notifyOnSave;
    setNotifyOnSave(false);
    return action(...args).then((result) => {
      if (shouldNotify) {
        onNotifyTeam().catch(() => showSnackbar("Saved, but couldn't notify the team - try again", "error"));
      }
      return result;
    });
  };
  const updateEventAndMaybeNotify = withMaybeNotify(onUpdateEvent);
  const reorderItemsAndMaybeNotify = withMaybeNotify(onReorderItems);
  const changeTemplateAndMaybeNotify = withMaybeNotify(onChangeTemplate);
  const addSongAndMaybeNotify = withMaybeNotify(onAddSong);
  const addNoteAndMaybeNotify = withMaybeNotify(onAddNote);
  const updateNoteAndMaybeNotify = withMaybeNotify(onUpdateNote);
  const deleteNoteAndMaybeNotify = withMaybeNotify(onDeleteNote);

  const commitField = (field, value, currentValue) => {
    if (value === currentValue) return;
    updateEventAndMaybeNotify({ [field]: value })
      .then(() => showSnackbar("Saved"))
      .catch(() => showSnackbar("Couldn't save - try again", "error"));
  };

  // Changing the start time can leave the end at or before it - the end
  // field is already pulled to two hours after the start while typing (see
  // the start field's onChange), and both are saved here in one update so
  // the team isn't notified twice. If the end was still after the start,
  // only the start is saved, as before.
  const commitStartTime = () => {
    const startChanged = timeStartDraft !== toDatetimeLocal(eventModal.timeStart);
    const endChanged = timeEndDraft !== toDatetimeLocal(eventModal.timeEnd);
    if (!startChanged) return;
    updateEventAndMaybeNotify({
      time_start: timeStartDraft,
      ...(endChanged && { time_end: timeEndDraft }),
    })
      .then(() => showSnackbar("Saved"))
      .catch(() => showSnackbar("Couldn't save - try again", "error"));
  };

  const commitName = () => {
    const trimmed = nameDraft.trim();
    // Name is required - revert to the last saved value rather than
    // ever committing an empty one. The field's own error/helperText
    // (see nameEditor) already shows this as invalid while it's empty.
    if (!trimmed) {
      setNameDraft(eventModal.title || "");
      return;
    }
    commitField("name", trimmed, eventModal.title);
  };

  const handleDelete = () => {
    setConfirmDeleteOpen(false);
    onDeleteEvent().catch(() => showSnackbar("Couldn't delete this event - try again", "error"));
  };

  const currentTemplateId = eventModal.setlistTemplate ? eventModal.setlistTemplate.id : null;
  // Which section's "add" editor is open, as `${droppableId}:song` or
  // `${droppableId}:note` - null when none is. Lifted up here (rather
  // than each control managing its own open state) so the two collapsed
  // triggers can sit on one compact row per section instead of two full
  // rows, and so opening one closes the other.
  const [activeAddControl, setActiveAddControl] = React.useState(null);

  // Applies immediately, no confirmation - switching templates isn't
  // destructive anymore (see EventSongPlacement/EventNotePlacement):
  // each template's own arrangement is remembered independently, so
  // switching away from one and back just brings it right back exactly
  // as it was. Only a template this event has never used before (or the
  // very first arrangement under any template) actually starts Unsorted.
  const handleTemplateSelectChange = (newTemplateId) => {
    changeTemplateAndMaybeNotify(newTemplateId).catch(() =>
      showSnackbar("Couldn't change the setlist template - try again", "error")
    );
  };

  const formatSongBlock = (song) => {
    const songTitle = `*${song.title}*\n`;
    const songArtist = song.artist ? `by ${song.artist}\n` : "";
    const startParam = song.youtube_start ? `?t=${song.youtube_start}` : "";
    // Both, when a song has both - YouTube first (it's the one people
    // expect to just tap and watch/listen from), Spotify after.
    const youtubeLink = song.youtube_id ? `https://youtu.be/${song.youtube_id}${startParam}\n` : "";
    const spotifyLink = song.spotify_id ? `https://open.spotify.com/track/${song.spotify_id}\n` : "";
    return songTitle + songArtist + youtubeLink + spotifyLink;
  };

  // "Share as text" (native share sheet) where the Web Share API exists -
  // mostly mobile - "Copy as text" (clipboard) otherwise. Same exact
  // WhatsApp-style formatted content either way (the *bold* markers are
  // WhatsApp's own syntax, not this app's) - only the delivery mechanism
  // changes, not what's produced. Copying (not just the native share
  // sheet) is included in the draft gate below too - on desktop,
  // copying this content is itself how someone shares it (paste into
  // WhatsApp/Slack/email), not a separate "just for me" action.
  const doShareOrCopyText = () => {
    const title = `*${eventModal.title}*`;
    const time =
      eventModal.timeStart
        .toLocaleString("en-GB", { timeZone: "UTC" })
        .replace(",", " at")
        .slice(0, -3) +
      "-" +
      eventModal.timeEnd
        .toLocaleTimeString("en-GB", { timeZone: "UTC" })
        .slice(0, -3);
    const details = eventModal.details ? eventModal.details : "";

    // No template: byte-for-byte the same as before this feature existed.
    // With one: section names become bold headers in the template's own
    // order, an empty section is skipped (no point pasting a bare
    // heading), and Unsorted items trail at the end with no header - the
    // same shape the no-template case already has. A note's content goes
    // in verbatim (no *title*/link wrapping) - what's typed is exactly
    // what's exported, same rule AddNoteControl's own placeholder shows.
    const formatItemBlock = (item) => (item.itemType === "note" ? item.content : formatSongBlock(item));

    // Same grouping used for rendering - with no template applied this
    // just means an empty `sections` and everything (songs and notes
    // both) falling into `unsorted`, so a template-less event's notes
    // now export the same way a templated one's do instead of being
    // silently dropped.
    const grouped = groupItemsByTemplate(eventModal.songs, eventModal.notes, eventModal.setlistTemplate);
    const sectionBlocks = grouped.sections
      .filter((section) => section.items.length > 0)
      .map((section) => `*${section.name}*\n` + section.items.map(formatItemBlock).join("\n"));
    const songList = sectionBlocks
      .concat(grouped.unsorted.map(formatItemBlock))
      .join("\n");

    const link = `*Event link*
${window.location.href}`;

    const content = [title, time, "", details, "", songList, "", link].join("\n");

    if (canNativeShare) {
      // Rejects with AbortError when the user just closes the share
      // sheet without picking anything - that's a cancel, not a
      // failure, so it doesn't get the error snackbar the way an
      // actual share failure does.
      navigator.share({ text: content }).catch((err) => {
        if (err && err.name === "AbortError") return;
        showSnackbar("Couldn't share - try again", "error");
      });
    } else {
      navigator.clipboard.writeText(content);
      showSnackbar("Copied to clipboard!");
    }
  };

  // Entry point for both "Share as text" buttons. A draft's link 404s
  // for anyone who isn't a leader/admin on this team (EventViewSet.
  // retrieve) - pausing to confirm here, before anything is shared,
  // beats finding that out after the fact.
  const handleShareOrCopyText = () => {
    if (eventModal.status === "draft") {
      setConfirmShareDraftOpen(true);
      return;
    }
    doShareOrCopyText();
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const grouped = groupItemsByTemplate(eventModal.songs, eventModal.notes, eventModal.setlistTemplate);
    const listFor = (droppableId) =>
      droppableId === "unsorted"
        ? grouped.unsorted
        : grouped.sections.find((section) => String(section.id) === droppableId).items;

    const sourceList = listFor(source.droppableId);
    const destList = listFor(destination.droppableId);
    const [moved] = sourceList.splice(source.index, 1);
    const newSection =
      destination.droppableId === "unsorted" ? null : parseInt(destination.droppableId, 10);
    destList.splice(destination.index, 0, { ...moved, section: newSection });

    // Splicing above only changed each item's *array position* - its
    // `order` field is still whatever it was before the drag. Without
    // reassigning `order` here too, the optimistic local update below
    // would carry stale values, and groupItemsByTemplate's `.sort((a, b)
    // => a.order - b.order)` on the next render would silently rebuild
    // the original order - the drag would visually snap right back,
    // even though the background request (which does compute a fresh
    // order) went out correctly.
    const flatItems = [
      ...grouped.sections.flatMap((section) => section.items),
      ...grouped.unsorted,
    ].map((item, index) => ({ ...item, order: index }));
    reorderItemsAndMaybeNotify(flatItems).catch(() =>
      showSnackbar("Couldn't save the new order - try again", "error")
    );
  };

  const handlePlaylistPlay = () => {
    const playlistLink =
      "https://www.youtube.com/watch_videos?video_ids=" +
      eventModal.songs.map((song) => song.youtube_id).join();
    window.open(playlistLink, "_blank");
  };

  if (eventModal.notFound) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={8} px={2} textAlign="center">
        <Typography variant="h6" gutterBottom>
          {eventModal.draftNotPublished ? "This event hasn't been published yet" : "Can't find this event"}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {eventModal.draftNotPublished
            ? "It's still a draft. We've let your team leaders know, so check back soon."
            : "It may belong to a different team than the one you're currently on."}
        </Typography>
        <Box mt={2}>
          <Button variant="outlined" onClick={closeEventModal}>
            Go back
          </Button>
        </Box>
      </Box>
    );
  }

  if (eventModal.loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  const timeStart = moment(eventModal.timeStart).utc().format("DD MMM YYYY HH:mm");
  const duration = durationString(eventModal.timeStart, eventModal.timeEnd);
  const canEdit = !!(user.is_staff | user.is_team_leader);
  const showEditControls = canEdit && isEditing;
  const hasSongs = eventModal.songs.length > 0;
  const hasNotes = eventModal.notes.length > 0;
  // Always true now - even a template-less event with nothing on it
  // yet still needs to render (the Add song/Add text controls), or
  // there'd be no way to add its first item without applying a template
  // first. Kept as its own name since setlistHeading below still reads
  // it, not because it's conditional anymore.
  const showSetlistArea = true;

  const renderSongRow = (song, { thumbLarge, showDivider, draggableRef, draggableProps, dragHandleProps } = {}) => (
    <ListItem
      key={song.id}
      ref={draggableRef}
      button
      divider={showDivider}
      onClick={() => history.push(`/songs/${song.id}`, { from: location })}
      {...draggableProps}
    >
      {dragHandleProps && (
        <span className={classes.dragHandle} {...dragHandleProps}>
          <DragIndicatorIcon fontSize="small" />
        </span>
      )}
      <ListItemAvatar>
        <SongThumbnail song={song} thumbLarge={thumbLarge} classes={classes} />
      </ListItemAvatar>
      <ListItemText
        primary={song.title}
        secondary={
          <>
            {song.artist}
            {hasLyrics(song) ? (
              <SubjectIcon
                titleAccess="Lyrics on file"
                className={classes.songBadge}
              />
            ) : (
              <NoLyricsWarning />
            )}
            {Array.isArray(song.bible_references) && song.bible_references.length > 0 && (
              <MenuBookIcon
                titleAccess="Bible reference linked"
                className={classes.songBadge}
              />
            )}
          </>
        }
      />
      <ListItemSecondaryAction>
        {showEditControls && (
          <SongMenuButton song={song} actions={menuActions} event={eventModal} />
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );

  // A Droppable section's item list (songs and notes, interleaved) -
  // either whatever's already dropped into it, or a dashed placeholder so
  // it stays a visible, valid drop target even when empty (matters most
  // right after a template is first applied, when everything starts
  // Unsorted).
  const renderDraggableList = (droppableId, items, thumbLarge) => {
    const sectionId = droppableId === "unsorted" ? null : parseInt(droppableId, 10);
    return (
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <List
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={thumbLarge ? undefined : classes.songList}
          >
            {items.length === 0 ? (
              <Box className={classes.emptyDropzone}>
                <Typography variant="caption" color="textSecondary">
                  Drop something here
                </Typography>
              </Box>
            ) : (
              items.map((item, index) => (
                <Draggable
                  key={`${item.itemType}-${item.id}`}
                  draggableId={`${item.itemType}-${item.id}`}
                  index={index}
                  isDragDisabled={!showEditControls}
                >
                  {(draggableProvided) => {
                    const rowProps = {
                      showDivider: index !== items.length - 1,
                      draggableRef: draggableProvided.innerRef,
                      draggableProps: draggableProvided.draggableProps,
                      dragHandleProps: showEditControls ? draggableProvided.dragHandleProps : null,
                    };
                    return item.itemType === "note" ? (
                      <NoteRow
                        note={item}
                        canEdit={showEditControls}
                        {...rowProps}
                        onUpdate={(content) =>
                          updateNoteAndMaybeNotify(item.id, content).catch(() =>
                            showSnackbar("Couldn't save that text block - try again", "error")
                          )
                        }
                        onDelete={() =>
                          deleteNoteAndMaybeNotify(item.id).catch(() =>
                            showSnackbar("Couldn't delete that text block - try again", "error")
                          )
                        }
                      />
                    ) : (
                      renderSongRow(item, { ...rowProps, thumbLarge })
                    );
                  }}
                </Draggable>
              ))
            )}
            {provided.placeholder}
            {showEditControls &&
              (activeAddControl === `${droppableId}:song` ? (
                <AddSongControl
                  onAdd={(song) => {
                    addSongAndMaybeNotify(song.id, sectionId).catch(() =>
                      showSnackbar("Couldn't add that song - try again", "error")
                    );
                    setActiveAddControl(null);
                  }}
                  onCancel={() => setActiveAddControl(null)}
                />
              ) : activeAddControl === `${droppableId}:note` ? (
                <AddNoteControl
                  onAdd={(text) => {
                    addNoteAndMaybeNotify(text, sectionId).catch(() =>
                      showSnackbar("Couldn't add that text block - try again", "error")
                    );
                    setActiveAddControl(null);
                  }}
                  onCancel={() => setActiveAddControl(null)}
                />
              ) : (
                <Box className={classes.addChooserRow}>
                  <Button
                    size="small"
                    className={classes.addChooserButton}
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={() => setActiveAddControl(`${droppableId}:song`)}
                  >
                    Add song
                  </Button>
                  <Button
                    size="small"
                    className={classes.addChooserButton}
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={() => setActiveAddControl(`${droppableId}:note`)}
                  >
                    Add text
                  </Button>
                </Box>
              ))}
          </List>
        )}
      </Droppable>
    );
  };

  // Every event gets the same draggable "unsorted" list (droppableId
  // "unsorted", section null) whether or not it has a template applied -
  // that's what carries the Add song/Add text controls, so they're
  // available even before a template exists. A template just adds its
  // own sections above that same list; without one it's just the whole
  // setlist, with no "Unsorted" label needed since there's nothing else
  // to distinguish it from.
  const renderSetlist = (thumbLarge) => {
    const grouped = groupItemsByTemplate(eventModal.songs, eventModal.notes, eventModal.setlistTemplate);
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        {grouped.sections.map((section) => (
          <React.Fragment key={section.id}>
            <div className={classes.sectionHeader}>
              <Typography className={classes.sectionLabel}>{section.name}</Typography>
              <Typography variant="caption" className={classes.setlistCount}>
                {section.items.length} {section.items.length === 1 ? "item" : "items"}
              </Typography>
            </div>
            {renderDraggableList(String(section.id), section.items, thumbLarge)}
          </React.Fragment>
        ))}
        {eventModal.setlistTemplate && (
          <div className={classes.sectionHeader}>
            <Typography className={classes.unsortedLabel}>Unsorted</Typography>
          </div>
        )}
        {renderDraggableList("unsorted", grouped.unsorted, thumbLarge)}
      </DragDropContext>
    );
  };

  // Shared by both platforms - a plain viewer (canEdit or not) never
  // sees anything but this; DRAFT is moot for a regular member in
  // practice (the API hides draft events from them entirely, see
  // EventViewSet.get_queryset/retrieve), kept simple rather than
  // special-cased.
  const statusChips = (
    <>
      {eventModal.status === "draft" && (
        <Box mb={1}>
          <Chip
            label="Draft - only visible to leaders/admins"
            size="small"
            color="secondary"
            variant="outlined"
          />
        </Box>
      )}
      {eventModal.status === "cancelled" && (
        <Box mb={1}>
          <Chip label="Cancelled" size="small" variant="outlined" style={{ borderColor: "#f44336", color: "#f44336" }} />
        </Box>
      )}
    </>
  );

  const eventMeta = (
    <>
      {eventModal.team && (
        <Typography
          component="p"
          className={classes.team}
          style={{ color: eventModal.team.color, borderColor: eventModal.team.color }}
        >
          {teamLabel(eventModal.team)}
        </Typography>
      )}
      {statusChips}
      {eventModal.timeStart && (
        <Typography component="p" className={classes.info}>
          {timeStart}
        </Typography>
      )}
      {eventModal.timeEnd && (
        <Typography component="p" className={classes.info}>
          {"Duration: " + duration}
        </Typography>
      )}
      {eventModal.details && (
        <Typography component="p" className={classes.info} style={{ whiteSpace: "pre-wrap" }}>
          {eventModal.details}
        </Typography>
      )}
    </>
  );

  // Shared by both platforms - directly editable the whole time
  // showEditControls is true, same as nameEditor/detailsEditor/
  // statusEditor below. A plain viewer still sees which template is
  // applied, just can't change it (selecting a value here applies
  // immediately - see handleTemplateSelectChange - there's no separate
  // save step to begin with).
  const templateControl = (
    <Box className={classes.templateControl}>
      {showEditControls ? (
        <TemplateSelect value={currentTemplateId} onChange={handleTemplateSelectChange} />
      ) : (
        <div className={classes.nameDisplay}>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="textSecondary" component="p">
              Setlist template
            </Typography>
            <Typography variant="body2">
              {eventModal.setlistTemplate ? eventModal.setlistTemplate.name : "None"}
            </Typography>
          </Box>
        </div>
      )}
    </Box>
  );

  // The rest of these are also shared by both platforms, only ever
  // rendered when showEditControls is true - each commits on blur (see
  // commitField), matching timeEditor's own long-standing pattern.
  const nameEditor = (
    <TextField
      label="Name of event"
      color="secondary"
      fullWidth
      value={nameDraft}
      onChange={(e) => setNameDraft(e.target.value)}
      onBlur={commitName}
      error={!nameDraft.trim()}
      helperText={!nameDraft.trim() ? "Name can't be empty" : " "}
      className={classes.sidebarField}
    />
  );

  const timeEditor = (
    <>
      <TextField
        label="Start time"
        type="datetime-local"
        color="secondary"
        value={timeStartDraft}
        onChange={(e) => {
          const value = e.target.value;
          setTimeStartDraft(value);
          // An end that is now at or before the new start moves to two hours
          // after it; an end that is still after the start is left alone.
          const newEnd = endForNewStart(value, timeEndDraft);
          if (newEnd) setTimeEndDraft(newEnd);
        }}
        onBlur={commitStartTime}
        InputLabelProps={{ shrink: true }}
        fullWidth
        className={classes.sidebarField}
      />
      <TextField
        label="End time"
        type="datetime-local"
        color="secondary"
        value={timeEndDraft}
        onChange={(e) => setTimeEndDraft(e.target.value)}
        onBlur={() => commitField("time_end", timeEndDraft, toDatetimeLocal(eventModal.timeEnd))}
        InputLabelProps={{ shrink: true }}
        fullWidth
        className={classes.sidebarField}
      />
    </>
  );

  const detailsEditor = (
    <TextField
      label="Details"
      variant="outlined"
      color="secondary"
      multiline
      minRows={10}
      fullWidth
      value={detailsDraft}
      onChange={(e) => setDetailsDraft(e.target.value)}
      onBlur={() => commitField("details", detailsDraft, eventModal.details || "")}
      className={classes.sidebarField}
    />
  );

  const statusEditor = (
    <TextField
      select
      label="Status"
      color="secondary"
      fullWidth
      value={eventModal.status}
      onChange={(e) => {
        updateEventAndMaybeNotify({ status: e.target.value })
          .then(() => showSnackbar("Saved"))
          .catch(() => showSnackbar("Couldn't save - try again", "error"));
      }}
      helperText={statusHelperText[eventModal.status]}
      className={classes.sidebarField}
    >
      <MenuItem value="draft">Draft</MenuItem>
      <MenuItem value="published">Published</MenuItem>
      <MenuItem value="cancelled">Cancelled</MenuItem>
    </TextField>
  );

  // Same toggle button either platform's Edit/Done control uses - mobile
  // renders it as a Fab (see the return below), desktop as a plain
  // Button at the top of the status column.
  const editToggleLabel = isEditing ? "Done" : "Edit";
  const toggleEditing = () => {
    setIsEditing(!isEditing);
    // A checked-but-never-saved box shouldn't carry into the next
    // editing session (or linger after this one ends) - Done always
    // starts the next round fresh.
    setNotifyOnSave(false);
  };

  const totalItemCount = eventModal.songs.length + eventModal.notes.length;
  const setlistHeading = showSetlistArea && (
    <div className={classes.setlistHeader}>
      <Typography variant="overline">Setlist</Typography>
      <Typography variant="caption" className={classes.setlistCount}>
        {totalItemCount} {totalItemCount === 1 ? "item" : "items"}
      </Typography>
    </div>
  );

  // Shared app bar: back, title, Spotify. The content below it is a
  // genuinely different layout on desktop (three columns, no floating
  // button - it's not just a wider version of the mobile column).
  const header = (
    <AppBar position="sticky" color="default" className={classes.appBar} elevation={1}>
      <Toolbar className={classes.toolbar}>
        <IconButton edge="start" aria-label="back" title="Back" onClick={closeEventModal}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" className={classes.title}>
          {eventModal.title}
        </Typography>
        {eventModal.spotify_playlist_id && (
          <IconButton
            href={`spotify:playlist:${eventModal.spotify_playlist_id}`}
            title="Open in Spotify"
          >
            <SvgIcon>
              <path d="M19.098 10.638c-3.868-2.297-10.248-2.508-13.941-1.387-.593.18-1.22-.155-1.399-.748-.18-.593.154-1.22.748-1.4 4.239-1.287 11.285-1.038 15.738 1.605.533.317.708 1.005.392 1.538-.316.533-1.005.709-1.538.392zm-.126 3.403c-.272.44-.847.578-1.287.308-3.225-1.982-8.142-2.557-11.958-1.399-.494.15-1.017-.129-1.167-.623-.149-.495.13-1.016.624-1.167 4.358-1.322 9.776-.682 13.48 1.595.44.27.578.847.308 1.286zm-1.469 3.267c-.215.354-.676.465-1.028.249-2.818-1.722-6.365-2.111-10.542-1.157-.402.092-.803-.16-.895-.562-.092-.403.159-.804.562-.896 4.571-1.045 8.492-.595 11.655 1.338.353.215.464.676.248 1.028zm-5.503-17.308c-6.627 0-12 5.373-12 12 0 6.628 5.373 12 12 12 6.628 0 12-5.372 12-12 0-6.627-5.372-12-12-12z" />
            </SvgIcon>
          </IconButton>
        )}
      </Toolbar>
    </AppBar>
  );

  const shareDraftConfirmDialog = (
    <Dialog open={confirmShareDraftOpen} onClose={() => setConfirmShareDraftOpen(false)}>
      <DialogTitle>Share this draft?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          "{eventModal.title}" hasn't been published yet. Its link won't
          open for anyone outside your team's leaders/admins until you
          publish it. Publishing also lets the team know about the event.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmShareDraftOpen(false)}>
          Cancel
        </Button>
        <Button
          color="secondary"
          variant="contained"
          autoFocus
          onClick={() => {
            setConfirmShareDraftOpen(false);
            updateEventAndMaybeNotify({ status: "published" })
              .then(() => showSnackbar("Published - now you can share it"))
              .catch(() => showSnackbar("Couldn't publish - try again", "error"));
          }}
        >
          Publish first
        </Button>
        <Button
          color="secondary"
          onClick={() => {
            setConfirmShareDraftOpen(false);
            doShareOrCopyText();
          }}
        >
          {canNativeShare ? "Share Anyway" : "Copy Anyway"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const deleteConfirmDialog = (
    <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
      <DialogTitle>Delete this event?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will remove "{eventModal.title}" and cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDeleteOpen(false)} autoFocus>
          Cancel
        </Button>
        <Button style={{ color: "red" }} onClick={handleDelete}>
          Delete Event
        </Button>
      </DialogActions>
    </Dialog>
  );

  const saveSnackbar = (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={snackbar.open}
      onClose={() => setSnackbar({ ...snackbar, open: false })}
      autoHideDuration={snackbar.severity === "error" ? 4000 : 2000}
    >
      <Alert
        variant="filled"
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  if (isDesktop) {
    return (
      <>
        {header}
        <Container>
          <div className={classes.desktopGrid}>
            <aside className={classes.sidebar}>
              {statusChips}
              {showEditControls ? (
                nameEditor
              ) : (
                <Typography
                  variant="h6"
                  className={classes.nameText}
                  // nameText's flex: 1 is meant for nameDisplay's flex
                  // *row* (grow next to the edit icon) - this title
                  // instead sits directly in the sidebar's flex
                  // *column*, where flex: 1 means grow to fill the
                  // column's leftover height (stretched by the grid to
                  // match the setlist column) instead, pushing
                  // everything below it down.
                  style={{ marginBottom: 16, flex: "initial" }}
                >
                  {eventModal.title}
                </Typography>
              )}
              {showEditControls ? (
                timeEditor
              ) : (
                <>
                  {eventModal.timeStart && (
                    <Typography component="p" className={classes.info}>
                      {timeStart}
                    </Typography>
                  )}
                  {eventModal.timeEnd && (
                    <Typography component="p" className={classes.info}>
                      {"Duration: " + duration}
                    </Typography>
                  )}
                </>
              )}
              {showEditControls ? (
                detailsEditor
              ) : (
                <div className={classes.nameDisplay}>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="textSecondary" component="p">
                      Details
                    </Typography>
                    <Typography component="div" style={{ whiteSpace: "pre-wrap" }}>
                      {eventModal.details || "No details"}
                    </Typography>
                  </Box>
                </div>
              )}
            </aside>

            <div className={classes.main}>
              {setlistHeading}
              {renderSetlist(true)}
            </div>

            <div className={classes.statusColumn}>
              {canEdit && (
                <div className={classes.editToggleRow}>
                  <Button
                    variant={isEditing ? "contained" : "outlined"}
                    color="secondary"
                    size="small"
                    startIcon={isEditing ? <CheckIcon /> : <EditIcon />}
                    onClick={toggleEditing}
                  >
                    {editToggleLabel}
                  </Button>
                  {showEditControls && eventModal.id && eventModal.status !== "draft" && (
                    <FormControlLabel
                      className={classes.notifyCheckbox}
                      control={
                        <Checkbox
                          size="small"
                          checked={notifyOnSave}
                          onChange={(e) => setNotifyOnSave(e.target.checked)}
                        />
                      }
                      label="Notify team on next save"
                    />
                  )}
                </div>
              )}
              {eventModal.team && (
                <Typography
                  component="p"
                  className={classes.team}
                  style={{ color: eventModal.team.color, borderColor: eventModal.team.color }}
                >
                  {teamLabel(eventModal.team)}
                </Typography>
              )}
              {templateControl}
              {(hasSongs || hasNotes) && (
                <div className={classes.sidebarActions}>
                  {hasSongs && (
                    <Button
                      variant="outlined"
                      startIcon={<YouTubeIcon />}
                      onClick={handlePlaylistPlay}
                      className={classes.sidebarButton}
                    >
                      Play full set
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={canNativeShare ? <ShareIcon /> : <FileCopyIcon />}
                    onClick={handleShareOrCopyText}
                    disabled={snackbar.open}
                    className={classes.sidebarButton}
                  >
                    {canNativeShare ? "Share as text" : "Copy as text"}
                  </Button>
                </div>
              )}
              {showEditControls && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!eventModal.spotify_playlist_id}
                        onChange={(e) => {
                          updateEventAndMaybeNotify({ spotify_playlist: e.target.checked })
                            .then(() => showSnackbar("Saved"))
                            .catch(() => showSnackbar("Couldn't save - try again", "error"));
                        }}
                      />
                    }
                    label="Create Spotify playlist?"
                  />
                  {statusEditor}
                  {eventModal.id && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteIcon />}
                      className={classes.deleteButton}
                      onClick={() => setConfirmDeleteOpen(true)}
                    >
                      Delete event
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </Container>

        {saveSnackbar}
        {deleteConfirmDialog}
        {shareDraftConfirmDialog}
        {(justPublished || viewerPromptEligible) && (
          <NotificationPermissionPrompt onClose={closeNotificationPrompt} />
        )}
      </>
    );
  }

  return (
    <>
      {header}

      <Container maxWidth="md" className={classes.content}>
        {showEditControls ? (
          <>
            {statusChips}
            {nameEditor}
            {timeEditor}
            {detailsEditor}
          </>
        ) : (
          eventMeta
        )}
        {templateControl}

        {(hasSongs || hasNotes) && (
          <div className={classes.actionsRow}>
            {hasSongs && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<YouTubeIcon />}
                onClick={handlePlaylistPlay}
              >
                Play full set
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={canNativeShare ? <ShareIcon /> : <FileCopyIcon />}
              onClick={handleShareOrCopyText}
              disabled={snackbar.open}
            >
              {canNativeShare ? "Share as text" : "Copy as text"}
            </Button>
          </div>
        )}

        {showEditControls && (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!eventModal.spotify_playlist_id}
                onChange={(e) => {
                  updateEventAndMaybeNotify({ spotify_playlist: e.target.checked })
                    .then(() => showSnackbar("Saved"))
                    .catch(() => showSnackbar("Couldn't save - try again", "error"));
                }}
              />
            }
            label="Create Spotify playlist?"
          />
        )}
        {showEditControls && statusEditor}
        {showEditControls && eventModal.id && eventModal.status !== "draft" && (
          <FormControlLabel
            control={
              <Checkbox
                checked={notifyOnSave}
                onChange={(e) => setNotifyOnSave(e.target.checked)}
              />
            }
            label="Notify team on next save"
          />
        )}

        {setlistHeading}
        {renderSetlist(false)}

        {showEditControls && eventModal.id && (
          <Box mt={3}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon />}
              className={classes.deleteButton}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete event
            </Button>
          </Box>
        )}
      </Container>

      {canEdit && (
        <Fab
          size="medium"
          aria-label={editToggleLabel + " event"}
          title={editToggleLabel}
          className={classes.fab}
          onClick={toggleEditing}
        >
          {isEditing ? <CheckIcon /> : <EditIcon />}
        </Fab>
      )}

      {saveSnackbar}
      {deleteConfirmDialog}
      {shareDraftConfirmDialog}
      {(justPublished || viewerPromptEligible) && (
          <NotificationPermissionPrompt onClose={closeNotificationPrompt} />
        )}
    </>
  );
};
